import axios from 'axios'
import qs from 'qs'
import type { AxiosRequestHeaders, AxiosRequestConfig, AxiosError } from 'axios'
import type { HttpResult, APIPromise, PromiseFn, APIConfig } from './types'
import { HttpStatus } from './types'
import { wrapPromise } from './utils/wrapPromise'
import { router } from '@/router/index'
import { getUserToken, pushEventTags } from '@/store/utils'

export function Get<T = any>(url: string, apiConfig?: APIConfig<T>): PromiseFn<T> {
  return (params?: any, headers: AxiosRequestHeaders = {}, config = {}) =>
    _request<T>(
      {
        url,
        method: 'get',
        params: params || null,
        headers: {
          Accept: 'application/json',
          ...headers,
        },
        ...config,
      },
      apiConfig
    )
}

export function Download<T = Blob>(url: string): PromiseFn<T> {
  return (params?: any, headers: AxiosRequestHeaders = {}) =>
    axios({
      url,
      method: 'get',
      params: params || null,
      responseType: 'blob',
      headers: {
        'xa-token': getUserToken(),
        ...headers,
      },
    }).then(response => response.data)
}

export function PostDownload<T = Blob>(url: string): PromiseFn<T> {
  return (body?: any, headers: AxiosRequestHeaders = {}) =>
    axios({
      url,
      method: 'post',
      data: body,
      responseType: 'blob',
      headers: {
        'xa-token': getUserToken(),
        ...headers,
      },
    }).then(response => response.data)
}

export function Post<T = any>(url: string, apiConfig?: APIConfig<T>): PromiseFn<T> {
  return (body?: any, headers: AxiosRequestHeaders = {}): Promise<any> =>
    _request<T>(
      {
        url,
        method: 'post',
        headers: {
          Accept: 'application/json',
          ...headers,
        },
        data: body,
      },
      apiConfig
    )
}

export function PostForm<T = any>(url: string, apiConfig?: APIConfig<T>): PromiseFn<T> {
  return (body?: any, headers: AxiosRequestHeaders = {}) =>
    _request<T>(
      {
        url,
        method: 'post',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          ...headers,
        },
        data: qs.stringify(body),
      },
      apiConfig
    )
}

export function Delete<T = any>(url: string, apiConfig?: APIConfig<T>): PromiseFn<T> {
  return (body?: any, headers: AxiosRequestHeaders = {}) =>
    _request<T>(
      {
        url,
        method: 'delete',
        headers: {
          Accept: 'application/json',
          ...headers,
        },
        data: body,
      },
      apiConfig
    )
}

export function Put<T = any>(url: string, apiConfig?: APIConfig<T>): PromiseFn<T> {
  return (body?: any, headers: AxiosRequestHeaders = {}) =>
    _request<T>(
      {
        url,
        method: 'put',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...headers,
        },
        data: body,
      },
      apiConfig
    )
}

function _request<T>(options: AxiosRequestConfig, apiConfig?: APIConfig<T>): APIPromise<T> {
  const _preRouterName = router.currentRoute.value.name

  // 自定义header
  const token = getUserToken()
  if (token) options.headers['xa-token'] = token
  if (!apiConfig?.noHeader) {
  }

  const promise: Promise<T> = new Promise((resolve, reject) => {
    axios({ timeout: apiConfig?.timeout, ...options })
      .then(({ data: { data, success, msg, code } }: HttpResult<T>) => {
        if (code === 401) {
          // 模拟 http 403
          throw {
            message: msg,
            response: { status: HttpStatus.NoLogin },
          }
        }
        const isSuccess = success === true || code === 200

        // 增加数据预处理方法
        if (isSuccess && apiConfig?.parser) apiConfig.parser(data)

        isSuccess ? resolve(data) : reject((() => {
          console.error(new Error(msg || '未知错误'))
          return {code: code, message: msg}
        })())
      })
      .catch(({ response, message }: AxiosError) => {
        let errorMsg = ''
        if (!response) {
          errorMsg = (/timeout/i.test(message) && '服务器响应超时') || '网络连接失败'
        } else {
          const { status, data: err } = response
          const { name: routerName, fullPath } = router.currentRoute.value
          // 请求结束还在原页面
          const isSameRoute = _preRouterName === routerName
          switch (status) {
            case HttpStatus.NoLogin:
              if (isSameRoute && routerName !== 'login') {
                router.replace({ name: 'login', query: { goto: fullPath } })
              }
              errorMsg = '请登录'
              break

            // 没权限(状态码为405、路由为403)
            case HttpStatus.NoPermission:
              if (isSameRoute && routerName !== '403' && /get/i.test(options.method)) {
                router.replace({ name: '403' })
              }
              errorMsg = '缺少访问权限'
              break

            case HttpStatus.NotFound:
              errorMsg = '接口不存在'
              break
            default:
              pushEventTags('error', options.url)
              errorMsg = err?.msg || message
          }
        }
        reject(new Error(errorMsg))
      })
  })
  return wrapPromise<T>(promise)
}

/**
 * JSONP
 * @param {String} url url中需存在{callback}模板以替换回调
 * eg. Jsonp(`https://apis.map.qq.com/ws/location/v1/ip?key=RIGBZ-ZTTWO-OQKWX-S33OV-NEJKH-W6FXB&callback={callback}&output=jsonp`)
 */
export function Jsonp<T = any>(url: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const _cbFnName = `cb_${Math.random().toString()}`
    const _script = document.createElement('script')
    _script.src = url.replace('{callback}', _cbFnName)
    _script.onload = () => {
      document.head.removeChild(_script)
    }
    _script.onerror = () => {
      document.head.removeChild(_script)
      reject(new Error('请求失败'))
    }
    ;(window as any)[_cbFnName] = (rs: T) => {
      resolve(rs)
      delete (window as any)[_cbFnName]
    }
    document.head.appendChild(_script)
  })
}
